import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import mediaFilesService from '../api/mediaFilesService';
import type { MediaFile, UpdateMediaFileDto } from '../types/types';

export const useMediaFiles = () => {
    return useQuery<MediaFile[]>({
        queryKey: ['mediaFiles'],
        queryFn: () => mediaFilesService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useMediaFile = (id: number | undefined) => {
    return useQuery<MediaFile>({
        queryKey: ['mediaFile', id],
        queryFn: () => mediaFilesService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateMediaFile = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ name, file }: { name: string; file: File }) => mediaFilesService.create(name, file),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['mediaFiles'] }),
    });
};

export const useUpdateMediaFile = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateMediaFileDto }) =>
            mediaFilesService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['mediaFiles'] });
            qc.invalidateQueries({ queryKey: ['mediaFile', id] });
        },
    });
};

export const useDeleteMediaFile = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => mediaFilesService.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['mediaFiles'] });
            qc.invalidateQueries({ queryKey: ['postureInstructions'] });
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testCategories'] });
        },
    });
};
